import { QTECode } from '~/core/constants/qte-constants';
import { ChipMinimizerModel } from '~/core/models/chip-minimizer.model';

export const DEFAULT_TITLE = 'Easy Estimator';

export enum IMAGE_SIZE {
  SMALL = 300, // or default
  MEDIUM = 600,
  LARGE = 1200,
  EXTRA_LARGE = 2400,
}

/**
 * Enum for the different versions of the estimate data
 */
export enum VERSIONS {
  UNKNOWN,
  V1,
  V2,
  V3,
}

export const EXPIRED_NOTIFICATION_KEY = `notification-${Date.now()}`;

// these are other storage keys that won't be cleared at initialization
export enum LOCAL_STORAGE_KEY {
  REMEMBER_ME = 'remember_me',
  SAVED_CUSTOMER_DATA = 'saved_customer_data',
  SAVED_ESTIMATE = 'saved_estimate',
  TOKEN = 'token',
  BASE_URL = 'baseURL',
  SECTIONS = 'sections',
  LOAD_ESTIMATE = 'loadEstimate',
  NOTIFICATIONS = 'notifications',
  IS_DIRTY = 'isEstimateModified',
  SEARCH_PARAMS = 'searchParams',
  KEEP_LOAD_ESTIMATE_IN_URL = 'keepLoadEstimateInURL',
  CODE = 'code',
  PDF_TYPE = 'pdfType',
  USER_TOKEN = 'userToken',
  PASSWORD = 'password',
  FIRST_SHAPE_LOAD = 'firstShapeLoad',
  ESTIMATE_CODE_SWITCH = 'estimateCodeSwitch',
  LOOKUP_PARAMS = 'lookupRetailerParams',
}

export const AXIOS_TIMEOUT = 2000;

// this is our internal order status, separate from the external handler (e.g. IME)
export enum STATUS {
  OPEN = 'Open',
  CLOSED = 'Closed',
  EXPIRED = 'Expired',
  HOLD = 'Hold',
  ERROR = 'Error',
}

export const DEFAULT_SAVE_ACTION = null; // the default value sent for a generic save
export type SaveActionType = string | typeof DEFAULT_SAVE_ACTION;

// this is the IME specific status of their workorder
export enum IME_STATUS {
  APPOINTMENT_PENDING = 'APPOINTMENT_PENDING', // lead created but not scheduled, not available to quote/revise
  APPOINTMENT_SET = 'APPOINTMENT_SET', //lead is scheduled and ready to quote/revise
  APPOINTMENT_RUN = 'APPOINTMENT_RUN', // lead has been submitted, but not ready for sale yet (should not remain in this state long), still ready to quote/revise
  SALE_PENDING = 'SALE_PENDING', // paymentURL has been provided, but sale is not completed, still ready to quote/revise
  CLOSED = 'CLOSED', //closed, final
  CLOSED_PENDING = 'CLOSED_PENDING', //pending close, not available to quote/revise
  SALE = 'SALE', // sale completed, not available to quote/revise
  SIT_NO_SALE_PENDING = 'SIT_NO_SALE_PENDING', // ??? unknown ???
}

// this is the explicit list of IME STATUS values that are valid for editing
export const EDITABLE_IME_STATUS = [
  IME_STATUS.APPOINTMENT_SET,
  IME_STATUS.APPOINTMENT_RUN,
  IME_STATUS.SALE_PENDING,
];

/**
 * the area range limits used to define small-job-ness
 * if AREA > LOW and < (either the material-defined-limit OR DEFAULT_HIGH) then it's a small job
 * @property LOW the minimum area for a small job
 * @property DEFAULT_HIGH the default maximum area for a small job, but use useSmallJobs() to get the actual area
 */
export const SMALL_JOB_AREA = {
  LOW: 10,
  DEFAULT_HIGH: 25,
};

export enum EE_STATUS {
  OPEN,
  CLOSED,
  NO_SAVE,
  ERROR,
  CAN_PAY,
}

export enum STATUS_REFRESH {
  OFF = 0,
  MIN = 5,
  MAX = 60,
  BACK_OFF = 1.25, // back off increase multiplier, <=1 == don't back off
  MAX_ERRORS = 10, //maximum number of errors to tolerate before we stop autoupdating
  MAX_ATTEMPTS = 20, // max number of successful attempts before we stop checking
}

// Cards Text
export const NO_TEAR_OUT = 'I will complete tear-out'; // title for the "no tear out" option card

// used to remove cutouts from the list of user-specified-cutouts
// it's a regex to check for word boundaries
// this should be re-visited with the customer as possibly outdated
export const FREE_CUT_OUT = /\bPolished\b/;

// Tooltip placement on different cards
export const TOOLTIP_PLACEMENT = 'rightTop';

// color text and  filters
export const DISCONTINUED = 'discontinued';
export const SEPARATOR_CHARACTER = ',';
export const BLACK = '#000000';

export enum NEGATIVE_PRODUCT_IDS {
  FREE_CORNER_RADIUS = -999,
  NO_TEAR_OUT = -998,
  OUTSIDE_DIAGONAL = -997,
  REMOVE_DIAGONAL = -996,
}

// Pricing Area Text
export const LIST_ITEM = 'List_item';
export const FREE_CORNER_RADIUS = {
  ID: NEGATIVE_PRODUCT_IDS.FREE_CORNER_RADIUS,
  LIST_ITEM: 'Free_Corner_item',
  NAME: 'Radius ¾ inches',
};

// Unit measures and currency
export const INCH_QUOTE = '″'; // ” Closing double quote WIN:alt+0148 MAC:option+shift+[ HTML:&#8221;
const INCH = 'in.';
const LININCH = 'lin. in.';
const EACH = 'ea.';
const SQFT = 'sq. ft.';

export const UNIT_MEASURE = {
  MATERIALS: SQFT,
  EDGE: LININCH,
  SIDE_WALLS: INCH,
  CORNER_RADIUS: EACH,
  DIAGONAL_CORNER: EACH,
  CUTOUT: EACH,
  DISHWASHER: EACH,
  REFRIGERATOR: EACH,
  TEAR_OUT: SQFT,
  COOKING_APPLIANCE: EACH,
  SINK_LABOR: EACH,
  SINK: EACH,
  FAUCET: EACH,
  JOINT_POINT: EACH,
  LABOR: EACH,
  WARRANTY: EACH,
  CUSTOM_WORK: EACH,
  CHIP_MINIMIZER: EACH,
  OVERSIZE: EACH,
  NONE: '', // for an edge case but seemed better to add it here
};

// TO DO: make currency switch dynamic at runtime, using a static file on server
export const CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
// Specifically used for Material cost ranges
export const SPACE = ' '; // code formatter removes the static text, so I added this
export const RANGE_SEPARATOR = ' - '; // code formatter was messing this up too
export const CURRENCY_NO_CENTS = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

// Drawing Area Text
export const BACKSPLASH_DEFAULT_VALUE = 4;
export const BACKSPLASH_INPUT_LIMIT = {
  MIN: 1,
  MAX: 60,
};
export const SHAPE_NAME_MAX_LENGTH = 20;
export const MEASUREMENT_LABEL_MAX_LENGTH = 9;

export enum KEYS {
  ENTER = 'Enter',
  TAB = 'Tab',
}

export const BACKSPLASH_ID = 1;
export const DRAWING_TABS = {
  PREDEFINED_SHAPE: 'PREDEFINED SHAPE',
  DRAW_LAYOUT: 'DRAW LAYOUT',
};

//constants for constraints
/**
 * Granularity is a measure of how divisible a property is.
 * In this case the length of the arrows of a shape.
 * @example
 * // If this value is 0.25 an arrow length may take the value 1.25, 1.5 and so on, in 0.25 increments. But not 1.3.
 */
export const ARROW_LENGTH_GRANULARITY_IN_INCHES = 0.25;

/**
 * Granularity is a measure of how divisible a property is.
 * In this case the angle of the corners radius.
 * @example
 * // If this value is 0.25 a corner radius angle may be  1.25, 1.5 and so on, in 0.25 increments. But not 1.3.
 */

export const CORNER_RADIUS_ANGLE_GRANULARITY_IN_DEGREES = 0.25;
export const MINIMUM_ANGLE_TO_ENFORCE_GAP = 85;
export const MINIMUM_GAP = 30; // The minimum gap between two counters
export const MINIMUM_CUSTOM_SIDE_LENGTH = 3;
export const CONSTRAINT_REFINEMENT_START_MARGIN = 0.5;

export const ISLAND_MINIMUM_AREA = 25;

export const CORNER_RADIUS_SIGN = 'r';
export const DEFAULT_CORNER_RADIUS = 0.75;
export const SELECTION_CIRCLE_RADIUS = 3;
export const SELECTION_CIRCLE_OPACITY = 0.6;
export const DEFAULT_DEPTH = 25.5;
export const FREE_DRAWING_SHAPE_MINIMUM_LENGTH = 25.5;
export const SHAPE_ROTATION_DEGREES = 45;
export const SLAB_SCALE = {
  DEFAULT: 3,
  MIN: 3,
  MAX: 30,
  POLYN_A: 0.000578,
  POLYN_B: -0.28,
  POLYN_C: 35,
  WIDTH_ADJUST: 0.7,
};
export const DEFAULT_LARGEST_SHAPE_DIMENSION = 100;
export const SWATCH_SCALE = 4;
export const LABEL_LEFT_COLLISION_MARGIN = 3;
export const SHAPE_LABEL_MARGIN = 2;
export const SHAPE_LABEL_ALTERED_CORNER_ADJUSTER_DENOMINATOR = 1.6;
export const LABEL_ELLIPSIS_LIMIT = 4;
export const ROUGH_ESTIMATE_FOR_SHAPE_LABEL_LENGTH = 26;
export const DEFAULT_RADIUS_SAGITTA = 12;
export const DEFAULT_BUMPOUT_DEPTH = 3;
export const DEFAULT_BUMPOUT_LENGTH = 36;
export const EDGE_LENGTH_INPUT_LIMIT = {
  MIN: 1,
  MAX: 300,
};
export const MAX_RADIUS_SAGITTA = 120;
export const DEFAULT_INTERNAL_DIAGONAL_CORNER_LENGTH = 12;
export const DEFAULT_EXTERNAL_DIAGONAL_CORNER_LENGTH = 3;

export const DEFAULT_ZOOM = 4; // default zoom level
export const MINIMUM_ZOOM = 0.1; // minimum zoom level
export const ZOOM_MARGIN_ADJUSTMENT = 0.98; // scale the auto-zoom to this percentage to provide for a border
export const ZOOM_PCT_CHANGE = 0.1;
export const AUTO_ZOOM_DELAY_LOOPS = 100;
export const OBJECT_SORT_DELAY_LOOPS = 100;
export const TEN_THOUSAND = 10000;
export const A_SMIDGEN = 0.0001;
export const RETURN_FROM_SUMMARY_STACK_PAUSE = 10000;
// the following "magic number" (introduced long ago) just work.
// TODO: figuring out the reason they work will likely generalize to angles beyond 90 and 135, etc.
export const BATWING_ANGLE = 135;
export const DEFAULT_ANGLE = 90;
export const MAGIC_NUMBER = 1.705;

/**
 * Factor used to calculate the equal sides of an isosceles triangle with a 135° (batwing) angle.
 *
 * @remarks
 * The value approximates the exact ratio:
 * `1 / (2 * sin(135° / 2)) ≈ 0.541196`
 *
 * The exact expression in JavaScript is:
 * `1 / (2 * Math.sin((135 / 2) * (Math.PI / 180))) ≈ 0.541196`
 *
 * @example
 * // For an isosceles triangle with vertex angle 135°, each equal side (l) is:
 * l = hypotenuse * 0.541196
 *
 * @note
 * While this fraction provides a convenient shortcut, using `sin(67.5°)`
 * directly would give slightly more precise results.
 */
export const BATWING_ISOSCELES_SIDE_FACTOR = 0.541196;

export const DEFAULT_EDGE_NAME = 'Eased Edge';
export const BUMPOUT_CLOCKWISE_REMAINDER = 0;
export const BUMPOUT_LENGTH = 1;
export const BUMPOUT_COUNTERCLOCKWISE_REMAINDER = 2;

export enum CANVAS_OBJECT_TYPE_PREFIX {
  ACTIVE_SHAPE_HIGHLIGHTS = 'active_shape_highlights',
  APPLIANCE = 'appliance',
  BACKSPLASH_ARROW = 'backsplash_arrow',
  BACKSPLASH_GROUP = 'backsplash_group',
  BACKSPLASH_LABEL = 'backsplash_label',
  BACKSPLASH_MEASURE_LABEL = 'backsplash_label_measure',
  BUMP_OUT = 'bump_out',
  BUMPOUT_ARROW = 'bumpout_arrow',
  CORNER_RADIUS_ARC = 'corner_radius_arc',
  CORNER_RADIUS_CIRCLE_FOR_DIAGONAL = 'corner_radius_circle_for_diagonal',
  CORNER_RADIUS_CIRCLE_FOR_ROUNDING = 'corner_radius_circle_for_rounding',
  CORNER_RADIUS_CIRCLE = 'corner_radius_circle',
  CORNER_RADIUS_MEASUREMENT = 'corner_radius_measurement',
  DIAGONAL = 'diagonal',
  EDGE_NAME = 'edge_name',
  FREE_DRAWING_TEMP_LENGTH = 'free_drawing_temporary_length',
  FREE_DRAWING_TEMP_SHAPE = 'free_drawing_temporary_shape',
  INTERIOR_ARROW = 'interior_arrow',
  POLYGON_BACKSPLASH = 'polygon_backsplash',
  RADIUS_OUT = 'radius_out',
  SELECTION_CIRCLE = 'selection_circle',
  SHAPE_EDGE = 'shape_edge',
  SHAPE_LABEL = 'shape_label',
  SHAPE_MAIN = 'shape_main',
  SHAPE_MEASURE_LABEL = 'shape_measure_label',
  ARROW_LABEL = 'arrow_label',
}
export enum CANVAS_OBJECT_TYPE_SUFFIX {
  SHAPE_GROUP = '_shape_group',
}

export const QUARTER_PART_NAME = {
  POS_X_AXIS: 'pos_x_axis',
  NEG_X_AXIS: 'neg_x_axis',
  POS_Y_AXIS: 'pos_y_axis',
  NEG_Y_AXIS: 'neg_y_axis',
  QUARTER_1: 'quarter_1', //x>0, y<0
  QUARTER_2: 'quarter_2', //x>0, y>0
  QUARTER_3: 'quarter_3', //x<0, y>0
  QUARTER_4: 'quarter_4', //x<0, y<0
};

// height of the bottom items (rotate, delete)
export const DESIGN_COUNTERTOP_ACTIONS_HEIGHT = 85;

export const GREY_STROKE_WIDTH = 0.75;
export const GREY_STROKE_WIDTH_BACKSPLASH = 0.4;

export enum SELECTION_CIRCLES {
  FOR_ROUNDED_RADII = 'rounded_radii',
  FOR_DIAGONAL_CORNERS = 'diagonal_corners',
  FOR_CUSTOM_SIDES = 'custom_sides',
  FOR_NONE = 'none',
}

export const CUSTOM_WORK_MODAL_MAX_QUANTITY = 10_000;
export const WATERFALL_DEFAULT_VALUE = 36;
export const WATERFALL_INPUT_LIMIT = {
  MIN: 21,
  MAX: 60,
};
export const WATERFALL_ICON =
  './public/assets/images/Waterfall/Waterfall_mitered_edge.png';
export const WATERFALL_ID = 2;

export const ABOVE_CORNER_FLAG = 'above';

export const sortAttPrice = 'price';
export const sortAttDesc = 'desc';
export const sortAttAsc = 'asc';
export const sortAttName = 'consumerDesc';
export const sortAttRank = 'rank';

// sets the maximum dimension for the generated JPG/PNG image(s)
export const MAX_IMAGE_EXPORT_DIMENSION = 1800;
export const MAX_IMAGE_EXPORT_BYTES = 4 * 1024 * 1024; // 6MB is absolute max for total API data

export const APPLIANCE_SELECTION_BORDER_THICKNESS = 4;

// These two images are for the info buttons in the shape tab
export const EDGE_OPTIONS_INFO_IMAGE =
  './public/assets/images/CountertopOptions/WallEdgePopup1.png'; // main info button
export const BACKSPLASH_INFO_IMAGE =
  './public/assets/images/CountertopOptions/BacksplashPopup.png'; // wall edge, backsplash button

export const UNDERMOUNT_FLAG = 'Undermount'; // mount type that indicates the need for chip minimizer
export const APRON_FRONT_FLAG = 'Apron Front'; // mount type that indicates the need for chip minimizer

export const CHIP_MINIMIZER_MOUNT_TYPES = [/Undermount/i, /Apron/i, /^Dual/i]; // mount type that indicates the need for chip minimizer

export const TRENDING_FLAG = 'trendingNow';
export const BEST_SELLING_FLAG = 'bestSelling';
export const BEST_SELLER_FLAG = 'bestseller';
export const IMAGE_CUSTOMER_SINK_APRON =
  '/public/assets/images/Appliances/CustomerSinks/Customer Apron Sink.png';
export const IMAGE_CUSTOMER_SINK_DOUBLE_BOWL =
  '/public/assets/images/Appliances/CustomerSinks/Double Bowl Sink.png';
export const IMAGE_CUSTOMER_DISHWASHER =
  '/public/assets/images/Appliances/DefaultDishwasher.png';
export const IMAGE_CUSTOMER_REFRIGERATOR =
  '/public/assets/images/Appliances/DefaultRefrigerator.png';
export const IMAGE_CUSTOMER_COOKTOP =
  '/public/assets/images/Appliances/CustomerCooktopsAndRanges/Customer Supplied Cooktop.png';
export const IMAGE_CUSTOMER_RANGE =
  '/public/assets/images/Appliances/CustomerCooktopsAndRanges/Customer Supplied Professional Range.png';
export const IMAGE_CUSTOMER_ELECTRICAL_CUTOUT =
  '/public/assets/images/Appliances/Cutouts/Electrical Cutout Icon.svg';
export const IMAGE_CUSTOMER_FAUCET_CUTOUT =
  '/public/assets/images/Appliances/Cutouts/Faucet Icon.svg';

export const IMAGE_RED_TRASH_CAN =
  '/public/assets/images/AddOnOptions/red-trash-can.png';

export const IMAGE_COMING_SOON = '/public/mock/images/coming_soon.svg';

export const FAUX_SHAPE_INDEX = 0;

const NO_CHIP_MINIMIZER_DESC = 'Decline Chip Minimizer';

export const NO_CHIP_MINIMIZER: ChipMinimizerModel = {
  abbrevDesc: NO_CHIP_MINIMIZER_DESC,
  consumerDesc: NO_CHIP_MINIMIZER_DESC,
  imageUrl: '/public/assets/images/ChipMinimizer/no_chip_minimizer.png',
  name: NO_CHIP_MINIMIZER_DESC,
  price: 0,
  productId: 0,
  unitOfMeasure: UNIT_MEASURE.CHIP_MINIMIZER,
  promos: [],
  qteTypeCode: null,
  qteTypeId: 0,
  qteCode: null,
  qteCodeId: 0,
  qteSubCode: null,
  qteSubCodeId: 0,
};

export const DEFAULT_EXPIRATION_DAYS = 60; // this is now only used if the back end does not provide a value (unlikely)
export const ELLIPSIS = '...';

export const INSIDE_DIAGONAL_CORNER_MINIMUM_LENGTH = 6;

/*
the descriptions come directly from the CAT_Feature table
+---------+-------------------------------+
|FeatureID|Name                           |
+---------+-------------------------------+
|20       |Movement                       |
|21       |Polished                       |
|22       |Non-Porous                     |
|23       |Scratch, Heat & Stain Resistant|
|24       |Outdoor/Indoor                 |
|25       |Already Sealed                 |
|26       |Renewable/Repairable           |
|27       |Stain Resistant                |
+---------+-------------------------------+

 */
interface MaterialAttributeMapElement {
  img: string;
  textKey: string;
}

enum COLOR_FEATURE {
  MOVEMENT = 'Movement',
  POLISHED = 'Polished',
  NON_POROUS = 'Non-Porous',
  SCRATCH = 'Scratch, Heat & Stain Resistant',
  INDOOR_OUTDOOR = 'Outdoor/Indoor',
  SEALED = 'Already Sealed',
  RENEWABLE = 'Renewable/Repairable',
  STAIN_RESISTANT = 'Stain Resistant',
  SUSTAINABLE = 'Sustainable',
  HYGIENIC = 'Hygienic',
  DURABLE = 'Durable',
  BUDGET_FRIENDLY = 'Budget Friendly',
}

type MaterialAttrMapType = {
  [key in COLOR_FEATURE]: MaterialAttributeMapElement;
} & { [key: string]: MaterialAttributeMapElement | undefined };

export const MATERIAL_ATTR_MAP: MaterialAttrMapType = {
  [COLOR_FEATURE.MOVEMENT]: {
    img: '/public/assets/viewSlabImages/Movement.png',
    textKey: 'MOVEMENT',
  },
  [COLOR_FEATURE.POLISHED]: {
    img: '/public/assets/viewSlabImages/Polished.png',
    textKey: 'POLISHED',
  },
  [COLOR_FEATURE.NON_POROUS]: {
    img: '/public/assets/viewSlabImages/Nonporous.png',
    textKey: 'NON_POROUS',
  },
  [COLOR_FEATURE.SCRATCH]: {
    img: '/public/assets/viewSlabImages/SCRATCH_HEAT_STAIN_RESIST.png',
    textKey: 'SCRATCH',
  },
  [COLOR_FEATURE.INDOOR_OUTDOOR]: {
    img: '/public/assets/viewSlabImages/Outdoor.png',
    textKey: 'INDOOR_OUTDOOR',
  },
  [COLOR_FEATURE.SEALED]: {
    img: '/public/assets/viewSlabImages/ALREADY_SEALED.png',
    textKey: 'SEALED',
  },
  [COLOR_FEATURE.RENEWABLE]: {
    img: '/public/assets/viewSlabImages/Renewable.png',
    textKey: 'RENEWABLE',
  },
  [COLOR_FEATURE.STAIN_RESISTANT]: {
    img: '/public/assets/viewSlabImages/Stain_Resistant.png',
    textKey: 'STAIN_RESISTANT',
  },
  [COLOR_FEATURE.SUSTAINABLE]: {
    img: '/public/assets/icons/sustainable.png',
    textKey: 'SUSTAINABLE',
  },
  [COLOR_FEATURE.HYGIENIC]: {
    img: '/public/assets/icons/hygienic.png',
    textKey: 'HYGIENIC',
  },
  [COLOR_FEATURE.DURABLE]: {
    img: '/public/assets/icons/durable.png',
    textKey: 'DURABLE',
  },
  [COLOR_FEATURE.BUDGET_FRIENDLY]: {
    img: '/public/assets/icons/budget-friendly.png',
    textKey: 'BUDGET_FRIENDLY',
  },
};

// Name of the product feature used to indicate whether it's an "undermount" sink
export const MOUNT_TYPE_FEATURE = 'mountType';

// These are used for cutout matching process for sinks and cutouts
export const DEFAULT_COOKTOP_QTECODEID = QTECode.Unpolished; // the qteCode to pass to the cutout selector for cooktops

export interface MaterialCutoutDefaultRule {
  name?: string; // name of the cutout identifier for this rule, internal use only
  materialType: string | RegExp | undefined;
  cutoutQteCodeId: QTECode; // ID of the qteCode to search for in cutoutLabor products to determine the selected cutout and cost
}

// default cutout qteCode per material
export const MATERIAL_CUTOUT_DEFAULTS: MaterialCutoutDefaultRule[] = [
  {
    name: 'Laminate',
    materialType: /laminate/gi,
    cutoutQteCodeId: QTECode.Unpolished,
  },
  {
    name: 'Solid Surface',
    materialType: /solid.*surface/gi,
    cutoutQteCodeId: QTECode.PolishedIntegratedSolidSurface,
  },
  {
    // this is the preferred fallback for all other materials
    name: 'Fallback - Polished',
    materialType: undefined, // matches all
    cutoutQteCodeId: QTECode.PolishedOther,
  },
  {
    // but if the previous isn't actually available, it will try this
    name: 'Fallback - Unpolished',
    materialType: undefined, // matches all
    cutoutQteCodeId: QTECode.Unpolished,
  },
];
